import { type BranchKey, configurationKeys } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { Alert, type Severity } from '@festina-life/ui/feedback'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useWorkspaceNavigation } from '../../../../system/useWorkspaceNavigation'
import type { MergeBranchInput } from './MergeBranchForm'
import {
  type FinalMergeStatus,
  isFinalMergeStatus,
  type MergeStatus,
} from './types'

const finalStatusToSeverity: Record<FinalMergeStatus, Severity> = {
  successWithConflicts: 'success',
  successNoConflicts: 'success',
  error: 'error',
}

type Props = {
  mergeStatus: MergeStatus
  mergeError: Optional<string>
  formValues: MergeBranchInput
  conflictsWorkspaceName: string
  targetBranchKey: BranchKey
  isTargetProtected: boolean
}

export const CurrentMergeStatus = ({
  mergeStatus,
  targetBranchKey,
  isTargetProtected,
  mergeError,
  formValues,
  conflictsWorkspaceName,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const navigateToWorkspace = useWorkspaceNavigation()
  const queryClient = useQueryClient()

  const isMerged = isFinalMergeStatus(mergeStatus)

  const finalStatusTranslations = useCallback(
    ({
      mergeBranchName,
    }: MergeBranchInput): Record<FinalMergeStatus, string> => ({
      successWithConflicts: translate(
        'branchDetailsPanel.tabs.manage.merge.enum.status.successWithConflicts',
      ),
      successNoConflicts: isTargetProtected
        ? translate(
            'branchDetailsPanel.tabs.manage.merge.enum.status.successNoConflicts',
            { mergeBranchName, targetBranchName: targetBranchKey.name },
          )
        : translate(
            'branchDetailsPanel.tabs.manage.merge.enum.status.successNoConflictsDirect',
            { targetBranchName: targetBranchKey.name },
          ),
      error: translate(
        'branchDetailsPanel.tabs.manage.merge.enum.status.error',
      ),
    }),
    [isTargetProtected, targetBranchKey.name, translate],
  )

  const handleNavigateToConflictWorkspace = async () => {
    await queryClient.invalidateQueries({
      queryKey: [configurationKeys.allConciseWorkspacesKeyPrefix],
    })

    const hostBranchName = isTargetProtected
      ? formValues.mergeBranchName
      : targetBranchKey.name

    await navigateToWorkspace(
      {
        name: conflictsWorkspaceName,
        branchKey: {
          name: hostBranchName,
          server: targetBranchKey.server,
        },
      },
      { mode: 'preserve' },
    )
  }

  return (
    <div className="flex flex-col gap-y-2">
      {mergeError && <Alert severity="error" title={mergeError} />}
      {mergeStatus && (
        <Alert
          severity={isMerged ? finalStatusToSeverity[mergeStatus] : undefined}
          isLoading={!isMerged}
        >
          {isMerged
            ? finalStatusTranslations(formValues)[mergeStatus]
            : translateEnum(
                'branchDetailsPanel.tabs.manage.merge.enum.status',
                mergeStatus,
              )}
        </Alert>
      )}
      {mergeStatus === 'successWithConflicts' && (
        <a
          className="cursor-pointer"
          onClick={handleNavigateToConflictWorkspace}
        >
          {conflictsWorkspaceName}
        </a>
      )}
    </div>
  )
}
